import { defineStore } from 'pinia';
import { useNotificationsStore } from '@/stores/useNotifications';
import { useAcvFetch } from '@/composables/useAcvFetch';
import useDatadogAddError from '@/composables/useDatadogAddError';
import { notificationKeys } from '@/constants/notificationKeys';

export const useCarrierUsersStore = defineStore({
  id: 'carrierUsers',
  state: () => {
    return {
      users: [],
      search: '',
      status: 'active',
      pagination: {
        size: 100,
      },
      currentPageIndex: 0,
      rowsPerPage: 100,
      dialog: {
        isDialogOpen: false,
        selectedUser: {},
        active: {
          dialogTitle: 'Delete User',
          dialogContent: '',
          acceptText: 'Delete User',
        },
        pending: {
          dialogTitle: 'Cancel Invitation',
          dialogContent: ``,
          acceptText: 'Send Cancel',
        },
      },
      updateUserRow: {
        isUpdateUserSheetOpen: false,
        selectedUser: {
          userName: '',
          phone: '',
          email: '',
          transUserId: 0,
          index: 0,
        },
      },
      testRow: {
        selectedUser: {},
      },
    };
  },
  actions: {
    async fetchUsers() {
      const { data, error } = await useAcvFetch('api/carrier/users', {
        params: {
          statuses: this.status,
          page: this.currentPageIndex,
          size: this.rowsPerPage,
          search: this.search,
        },
      });

      if (error.value) {
        // pop error
        return;
      }

      this.$patch({
        pagination: data.value.data.pagination,
        users: data.value.data.content,
      });
    },
    async inviteUsers(payload) {
      if (!payload) {
        return;
      }

      const { data, error } = await useAcvFetch('api/carrier/invite-user', {
        method: 'POST',
        body: { users: payload },
      });

      const notificationsStore = useNotificationsStore();

      if (error.value) {
        const customError = new Error('400 invite users');
        useDatadogAddError(customError, payload);

        const notificationParams = {
          notificationMessageTemplate:
            '<span class="tw-font-medium">Unable to add user.</span>  Please check user email and try again.',
          variant: 'error',
          key: notificationKeys.errorInviteUser,
        };
        notificationsStore.addNotification(notificationParams);

        return;
      }

      if (data.value) {
        const notificationParams = {
          notificationMessageTemplate:
            'Your invite has been sent requesting this user to <span class="tw-font-medium">Sign Up for ACV</span>',
          variant: 'success',
          key: notificationKeys.successInviteUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    async removeUser() {
      if (!this.dialog.selectedUser?.transUserId) {
        return;
      }

      const notificationsStore = useNotificationsStore();
      const { data, error } = await useAcvFetch('api/carrier/user', {
        method: 'DELETE',
        body: {
          id: this.dialog.selectedUser.transUserId,
        },
      });

      if (error.value) {
        const customError = new Error('404 delete user');
        useDatadogAddError(customError, {
          selectedUser: this.dialog.selectedUser,
        });

        const notificationParams = {
          notificationMessageTemplate:
            '<span class="tw-font-medium">Unable to delete user.</span>  Please check user email and try again.',
          variant: 'error',
          key: notificationKeys.errorDeleteUser,
        };
        notificationsStore.addNotification(notificationParams);

        return;
      }

      /**
       * If user is successfully removed update the user list by filtering user from array
       */
      if (data.value) {
        this.users = this.users.filter(
          (_, index) => index !== this.dialog.selectedUser.index
        );

        this.pagination.totalElements -= 1;

        this.dialog.selectedUser = {};

        const notificationParams = {
          notificationMessageTemplate: 'User has been successfully removed',
          variant: 'success',
          key: notificationKeys.successInviteUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    async updateUser() {
      const selectedUser = this.updateUserRow.selectedUser;
      if (!selectedUser.transUserId) {
        return;
      }

      const notificationsStore = useNotificationsStore();
      const { data, error } = await useAcvFetch('api/carrier/user', {
        method: 'PUT',
        body: {
          userId: selectedUser.transUserId,
          userName: selectedUser.userName,
          role: selectedUser.role,
          phone: selectedUser.phone,
        },
      });

      if (error.value) {
        const customError = new Error('500 update user');
        useDatadogAddError(customError, {
          selectedUser: selectedUser,
        });

        const notificationParams = {
          notificationMessageTemplate:
            '<span class="tw-font-medium">Unable to update user information.</span>  Please check your connection and try again.',

          variant: 'error',
          key: notificationKeys.errorUpdateUser,
        };
        notificationsStore.addNotification(notificationParams);

        return;
      }

      if (data.value) {
        const temp = this.users;

        this.users[selectedUser.index] = data.value.data;
        this.users = [...this.users];

        const notificationParams = {
          notificationMessageTemplate: 'User information has been updated.',
          variant: 'success',
          key: notificationKeys.successUpdateUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    updateStatus(newStatus) {
      if (newStatus) {
        this.$patch({
          status: newStatus,
          pagination: {},
          rowsPerPage: 100,
          currentPageIndex: 0,
          search: '',
        });
      }
    },
    updateSearch(newSearch) {
      this.$patch({
        search: newSearch,
      });
    },
    updateRowsPerPage(newRowsPerPage) {
      if (newRowsPerPage) {
        this.$patch({
          rowsPerPage: newRowsPerPage,
        });
      }
    },
    updatePageIndex(newPageIndex) {
      this.$patch({
        currentPageIndex: newPageIndex,
      });
    },
  },
  getters: {
    cancelPendingUserMessage: (state) =>
      `Do you want to cancel the invitation sent to ${state.dialog.selectedUser?.email}?`,
    cancelActiveUserMessage: () => 'Are you sure you want to delete this user?',
  },
});
